import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CHORD_CATEGORIES, CHORD_TYPES, type ChordTypeId } from '@/music/chords';
import { generateChordVoicing, voicingIdentityLabel, type Inversion } from '@/music/voicing';
import {
  isVoicingFamily,
  primaryVoicingFamily,
  voicingFamilyLabel,
  type VoicingFamily,
} from '@/music/voicingFamilies';
import { useStore } from '@/state/store';
import { stringLayoutFor } from '@/music/tuning';
import { AVAILABLE_VOICING_FAMILY_IDS, availableVoicingFamily } from '@/state/voicingAvailability';
import { NoteSelect } from '@/components/pickers';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { JoePassLibrary } from '@/components/JoePassLibrary';
import { ReferencePage } from '@/components/ReferencePage';
import { LibraryAlternativeShapes } from '@/components/AlternativeShapes';
import type { InspectChord } from '@/components/inspectorContext';

const INVERSIONS: Inversion[] = [0, 1, 2, 3];

export function LibraryPage() {
  const stringSet = useStore((s) => s.stringSet);
  const defaultFamily = useStore((s) => s.voicingFamily);
  const avoidB9 = useStore((s) => s.avoidB9);
  const [searchParams, setSearchParams] = useSearchParams();
  const [root, setRoot] = useState('C');
  const requestedFamily = searchParams.get('family');
  const [voicingFamily, setVoicingFamily] = useState<VoicingFamily>(() =>
    isVoicingFamily(requestedFamily)
      ? requestedFamily
      : primaryVoicingFamily(availableVoicingFamily(defaultFamily)),
  );

  useEffect(() => {
    setVoicingFamily(
      isVoicingFamily(requestedFamily)
        ? requestedFamily
        : primaryVoicingFamily(availableVoicingFamily(defaultFamily)),
    );
  }, [requestedFamily, defaultFamily]);

  const selectFamily = (family: VoicingFamily) => {
    setVoicingFamily(family);
    const next = new URLSearchParams(searchParams);
    next.set('family', family);
    setSearchParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    if (voicingFamily === 'joe-pass') return [];
    return CHORD_CATEGORIES.map((category) => ({
      ...category,
      chords: category.types.map((typeId: ChordTypeId) => ({
        typeId,
        voicings: INVERSIONS.map((inv) =>
          generateChordVoicing(root, typeId, inv, stringSet, voicingFamily),
        ),
      })),
    }));
  }, [root, stringSet, voicingFamily]);

  return (
    <ReferencePage
      className="library-page"
      title="Chord library"
      controls={
        <>
          <div className="control-group">
            <span className="label">Voicing family</span>
            <div className="segmented" role="group" aria-label="Voicing family">
              {AVAILABLE_VOICING_FAMILY_IDS.map((family) => (
                <button
                  key={family}
                  type="button"
                  className={voicingFamily === family ? 'active' : ''}
                  aria-pressed={voicingFamily === family}
                  onClick={() => selectFamily(family)}
                  data-testid={`library-family-${family}`}
                >
                  {voicingFamilyLabel(family)}
                </button>
              ))}
            </div>
          </div>
          {voicingFamily !== 'joe-pass' && (
            <div className="control-group">
              <span className="label">Root</span>
              <NoteSelect value={root} onChange={setRoot} aria-label="Root note" />
            </div>
          )}
        </>
      }
    >
      {voicingFamily === 'joe-pass' && <JoePassLibrary />}
      {rows.map((category) => (
        <section key={category.category}>
          <h2 className="section-title">
            {category.label.charAt(0) + category.label.slice(1).toLowerCase()}
          </h2>
          {category.chords.map(({ typeId, voicings }) => (
            <div key={typeId} className="assignment-group" data-testid={`library-chord-${typeId}`}>
              <h3>
                {root}
                {CHORD_TYPES[typeId].symbol} · {CHORD_TYPES[typeId].formula}
              </h3>
              <div className="library-inversion-grid">
                {voicings.map((voicing, inv) =>
                  voicing ? (
                    <PlayableDiagram
                      key={inv}
                      chord={toInspect(voicing, root, typeId, stringSet)}
                      caption={voicingIdentityLabel(voicing, root, typeId)}
                      showTitle={false}
                      highlightAvoid={avoidB9}
                    />
                  ) : (
                    <div key={inv} className="chord-card" aria-disabled>
                      <div className="card-caption">No voicing</div>
                    </div>
                  ),
                )}
              </div>
              <LibraryAlternativeShapes
                rootDisplay={root}
                chordType={typeId}
                initialFingering={
                  voicings.find((voicing) => voicing != null) ?? {
                    frets: [null, null, null, null, null, null],
                    voicing: [],
                    voicingFamily,
                    stringLayout: stringLayoutFor(voicingFamily, stringSet).id,
                    stringSet,
                  }
                }
                highlightAvoid={avoidB9}
              />
            </div>
          ))}
        </section>
      ))}

      {voicingFamily === 'drop-2' && (
        <section>
          <h2 className="section-title">Fourths</h2>
          <p className="section-note">
            Maj7♭5 and 7sus4 voicings built mostly from fourths. Captions give the bass and the top
            voice.
          </p>
          <div className="library-inversion-grid library-inversion-grid-short">
            {[
              { typeId: 'maj7b5' as ChordTypeId, inv: 0 as Inversion },
              { typeId: 'dom7sus4' as ChordTypeId, inv: 2 as Inversion },
            ].map(({ typeId, inv }) => {
              const voicing = generateChordVoicing(root, typeId, inv, stringSet, voicingFamily);
              return voicing ? (
                <PlayableDiagram
                  key={`${typeId}-${inv}`}
                  chord={toInspect(voicing, root, typeId, stringSet)}
                  caption={voicingIdentityLabel(voicing, root, typeId)}
                  showTitle={false}
                  highlightAvoid={avoidB9}
                />
              ) : null;
            })}
          </div>
        </section>
      )}
    </ReferencePage>
  );
}

function toInspect(
  voicing: NonNullable<ReturnType<typeof generateChordVoicing>>,
  root: string,
  typeId: ChordTypeId,
  stringSet: 'middle' | 'upper',
): InspectChord {
  return {
    fingering: voicing,
    rootDisplay: root,
    chordType: typeId,
    symbol: voicing.symbol,
    inversion: voicing.inversion,
    stringSet,
  };
}
